import type { AppContext } from "../app/app";
import { buildBackup } from "../data/backup";
import { el } from "./dom";
import { THEME_STORAGE_KEY } from "./theme";

const CONFIRM_WORD = "DELETE";

// A deliberately high-friction confirmation before erasing everything: a plain
// irreversible warning, the offer to export a backup first, and a typed-word
// gate (not a bare OK) before the destructive button enables.
export function confirmDeleteAll(ctx: AppContext): void {
  const overlay = el("div", { class: "consent" });
  const close = () => overlay.remove();

  const exportFirst = el("button", {
    class: "btn btn--ghost",
    text: "Export a backup first",
    on: {
      click: async () => {
        const file = await buildBackup(ctx.repo, Date.now());
        const stamp = new Date().toISOString().slice(0, 10);
        const blob = new Blob([JSON.stringify(file, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `trust-lens-backup-${stamp}.json`;
        a.click();
        URL.revokeObjectURL(url);
      },
    },
  });

  const confirmInput = el("input", {
    class: "input",
    type: "text",
    placeholder: `Type ${CONFIRM_WORD} to confirm`,
    attrs: { "aria-label": `Type ${CONFIRM_WORD} to confirm` },
  });

  const wipe = el("button", {
    class: "btn btn--danger",
    text: "Delete everything",
    on: {
      click: async () => {
        if (confirmInput.value.trim() !== CONFIRM_WORD) return;
        wipe.disabled = true;
        await ctx.repo.resetAll();
        // Clear app localStorage (theme is the only key) so nothing survives.
        try {
          localStorage.removeItem(THEME_STORAGE_KEY);
        } catch {
          // ignore: storage may be unavailable
        }
        // Return to first-run state by reloading: the app re-bootstraps with an
        // empty store and the onboarding gate fires.
        location.reload();
      },
    },
  });
  wipe.disabled = true;
  confirmInput.addEventListener("input", () => {
    wipe.disabled = confirmInput.value.trim() !== CONFIRM_WORD;
  });

  overlay.append(
    el(
      "div",
      { class: "consent__card", attrs: { role: "dialog", "aria-modal": "true" } },
      el("span", { class: "label", text: "Danger" }),
      el("h2", { class: "consent__title", text: "Delete all data?" }),
      el("p", {
        class: "lede",
        text:
          "This erases everyone you have added, every rating, event, decision, " +
          "domain flag and your settings from this browser. It cannot be undone.",
      }),
      el("p", {
        class: "lede",
        text: "If you might want this data back, export a backup before you delete.",
      }),
      el("div", { class: "danger__export" }, exportFirst),
      el("div", { class: "danger__confirm" }, confirmInput),
      el(
        "div",
        { class: "consent__actions" },
        wipe,
        el("button", { class: "btn btn--ghost", text: "Cancel", on: { click: close } }),
      ),
    ),
  );
  document.body.append(overlay);
}
